import React, { useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  TextField,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

interface Props {
  label?: string;
  value: Record<string, string | number> | undefined;
  onChange: (next: Record<string, string | number>) => void;
  onRemove?: () => void;
  keyLabel?: string;
  valueLabel?: string;
  valueType?: "string" | "number";
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  tagSuggestions?: string[];
}

const TagMapEditor: React.FC<Props> = ({
  label,
  value,
  onChange,
  onRemove,
  keyLabel = "Tag",
  valueLabel = "Value",
  valueType = "string",
  keyPlaceholder = "",
  valuePlaceholder = "",
  tagSuggestions,
}) => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;
  const isDark = theme.palette.mode === "dark";
  const [open, setOpen] = useState(false);
  const entries = Object.entries(value || {});

  const updateKey = (oldKey: string, newKey: string) => {
    const next: Record<string, string | number> = {};
    for (const [k, v] of entries) {
      next[k === oldKey ? newKey : k] = v;
    }
    onChange(next);
  };

  const updateValue = (key: string, newValue: string) => {
    const coerced: string | number =
      valueType === "number" ? (parseFloat(newValue) || 0) : newValue;
    onChange({ ...(value || {}), [key]: coerced });
  };

  const removeRow = (key: string) => {
    const next = { ...(value || {}) };
    delete next[key];
    onChange(next);
  };

  const addRow = () => {
    const current = value || {};
    // Pre-fill with the next unused standard tag if suggestions exist.
    if (tagSuggestions && tagSuggestions.length > 0) {
      const next = tagSuggestions.find((t) => !(t in current));
      if (next) {
        onChange({ ...current, [next]: "" });
        return;
      }
    }
    let placeholder = "newTag";
    let idx = 1;
    while (placeholder in current) {
      placeholder = `newTag${idx++}`;
    }
    onChange({ ...current, [placeholder]: "" });
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.75 }}
      >
        {label ? (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              fontFamily: "ui-monospace, Menlo, monospace",
            }}
          >
            {label}
          </Typography>
        ) : (
          <span />
        )}
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setOpen(true)}
            sx={{ textTransform: "none", color: accent, borderColor: accent }}
          >
            Edit
          </Button>
          {onRemove && (
            <IconButton
              size="small"
              onClick={onRemove}
              aria-label={`Remove ${label || "tag map"}`}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          p: 1.25,
          borderRadius: 1,
          border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
          backgroundColor: isDark
            ? "rgba(255,255,255,0.04)"
            : "rgba(0,0,0,0.025)",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12.5,
          lineHeight: 1.55,
          minHeight: 40,
        }}
      >
        {entries.length === 0 ? (
          <Typography
            variant="caption"
            sx={{ color: "text.disabled", fontStyle: "italic" }}
          >
            (empty — click Edit to add tags)
          </Typography>
        ) : (
          <Stack spacing={0.25}>
            {entries.map(([k, v]) => (
              <Box
                key={k}
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  color: "text.primary",
                }}
              >
                <Box sx={{ fontWeight: 600 }}>{k}</Box>
                <Box sx={{ color: "text.secondary" }}>→</Box>
                <Box
                  sx={{
                    color:
                      v === "" || v === null || v === undefined
                        ? "text.disabled"
                        : "text.primary",
                  }}
                >
                  {v === "" || v === null || v === undefined
                    ? "(empty)"
                    : String(v)}
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: accent, fontWeight: 600 }}>
          Edit {label || "Tag Map"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            {entries.length === 0 ? (
              <Typography
                variant="caption"
                sx={{ color: "text.disabled", fontStyle: "italic" }}
              >
                (empty — add a row below)
              </Typography>
            ) : (
              entries.map(([k, v], idx) => {
                const usedKeys = new Set(Object.keys(value || {}));
                const availableSuggestions = (tagSuggestions || []).filter(
                  (t) => !usedKeys.has(t) || t === k,
                );
                return (
                <Grid container key={idx} spacing={1} alignItems="center">
                  <Grid item xs={5}>
                    {tagSuggestions && tagSuggestions.length > 0 ? (
                      <Autocomplete
                        freeSolo
                        size="small"
                        forcePopupIcon
                        openOnFocus
                        selectOnFocus
                        handleHomeEndKeys
                        clearOnBlur={false}
                        options={availableSuggestions}
                        value={k}
                        onInputChange={(_, v2) => updateKey(k, v2 || "")}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={keyLabel}
                            placeholder="Pick or type custom"
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        size="small"
                        fullWidth
                        label={keyLabel}
                        placeholder={keyPlaceholder}
                        value={k}
                        onChange={(e) => updateKey(k, e.target.value)}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      fullWidth
                      type={valueType === "number" ? "number" : "text"}
                      label={valueLabel}
                      placeholder={valuePlaceholder}
                      value={v ?? ""}
                      onChange={(e) => updateValue(k, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton size="small" onClick={() => removeRow(k)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
                );
              })
            )}
          </Stack>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addRow}
            sx={{ mt: 1.5, textTransform: "none", color: accent }}
          >
            Add tag
          </Button>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            sx={{ textTransform: "none", color: accent, fontWeight: 600 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagMapEditor;
